import React, { useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';
import PersonalityTabs from './PersonalityTabs';

// Offset from top of chat window to position the last user message
const SCROLL_OFFSET_PX = 12;

function ChatWindow({
  conversation,
  isLoading,
  selectedPersonality,
  onPersonalityChange,
  showPersonalityTabs = false
}) {
  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);
  const prevLoadingRef = useRef(isLoading);
  const userInteractionTriggered = useRef(false);

  const scrollToLastUserMessage = () => {
    if (!chatWindowRef.current) return;

    // Find all user messages
    const userMessages = chatWindowRef.current.querySelectorAll('.message-user');

    if (userMessages.length > 0) {
      // Get the last user message
      const lastUserMessage = userMessages[userMessages.length - 1];

      // Calculate the scroll position to place the message at the specified offset from the top
      const chatWindowRect = chatWindowRef.current.getBoundingClientRect();
      const messageRect = lastUserMessage.getBoundingClientRect();
      const currentScrollTop = chatWindowRef.current.scrollTop;

      // Calculate where the message currently is relative to the chat window
      const messageTopRelativeToWindow = messageRect.top - chatWindowRect.top;

      // Calculate how much we need to scroll to get the message at the desired offset from the top
      const targetScrollTop = currentScrollTop + messageTopRelativeToWindow - SCROLL_OFFSET_PX;

      // Scroll to the calculated position
      chatWindowRef.current.scrollTop = targetScrollTop;
    } else {
      // If no user messages, scroll to bottom as fallback
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const triggerUserInteraction = () => {
    if (userInteractionTriggered.current) return;

    // Create an invisible button and click it to simulate user interaction
    const invisibleButton = document.createElement('button');
    invisibleButton.style.position = 'absolute';
    invisibleButton.style.top = '-9999px';
    invisibleButton.style.left = '-9999px';
    invisibleButton.style.width = '1px';
    invisibleButton.style.height = '1px';
    invisibleButton.style.opacity = '0';
    invisibleButton.style.pointerEvents = 'none';

    document.body.appendChild(invisibleButton);

    // Programmatically click the button
    setTimeout(() => {
      invisibleButton.click();
      invisibleButton.focus();
      invisibleButton.blur();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(invisibleButton);
      }, 100);
    }, 50);

    userInteractionTriggered.current = true;
  };

  const initializeScrolling = () => {
    if (chatWindowRef.current && window.innerWidth <= 800) {
      const element = chatWindowRef.current;

      // Trigger user interaction first
      triggerUserInteraction();

      // Then initialize scrolling
      setTimeout(() => {
        // Force a larger scroll movement
        const originalScrollTop = element.scrollTop;
        element.scrollTop = originalScrollTop + 10;
        element.scrollTop = originalScrollTop;

        // Ensure the element is focusable for touch events
        element.setAttribute('tabindex', '-1');

        // Force a style recalculation
        element.style.transform = 'translateZ(0)';
        void element.offsetHeight; // Force reflow
      }, 200); // Wait for user interaction to register
    }
  };

  // Initialize scrolling after streaming is complete
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    const isNowLoaded = !isLoading;

    // If we just finished loading (was loading, now not loading)
    if (wasLoading && isNowLoaded) {
      // Run scroll hack with delays after streaming completes
      const timers = [
        setTimeout(initializeScrolling, 100),
        setTimeout(initializeScrolling, 500),
        setTimeout(initializeScrolling, 1000)
      ];

      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }

    // Update the previous loading state
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    scrollToLastUserMessage();
  }, [conversation]);

  return (
    <div className={`chat-window ${isLoading ? 'loading' : ''}`} ref={chatWindowRef}>
      {showPersonalityTabs && (
        <PersonalityTabs
          selectedPersonality={selectedPersonality}
          onPersonalityChange={onPersonalityChange}
          disabled={isLoading}
        />
      )}
      <div className="chat-window-content">
        {conversation.map((message, index) => (
          <ChatMessage
            key={index}
            message={message}
            isFirst={index === 0}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

export default ChatWindow;
